/**
 * BRAND - Central Design System - CLUB WILD
 * Single source of truth for all colors, spacing, and design tokens
 *
 * BRAND STRATEGY:
 * - Orange (#E48F15) = Energy, adventure, warmth, fun
 * - Green (#3EA53E) = Nature, outdoors, growth, sustainability
 * - Fun, playful, child-friendly design for HAF programme
 * - Exciting outdoor adventure brand for kids aged 5-11
 *
 * USAGE RULES:
 * - ALWAYS import and use BRAND constants instead of hardcoding colors
 * - NEVER hardcode hex values directly in components
 * - Activity colors are available via BRAND.activities.bushcraft.primary etc.
 *
 * See: /docs/CODING_STANDARDS.md for full guidelines
 */
export const BRAND = {
  // Core Brand Colors - ORANGE + GREEN (ADVENTURE SYSTEM)
  orange: "#E48F15",           // Vibrant orange - primary brand color, energy, fun
  orangeLight: "#F5A73C",      // Light orange - hover states, highlights
  orangeDark: "#C67810",       // Dark orange - contrast elements, depth

  primary: "#E48F15",          // Orange - primary brand color
  primaryHover: "#C67810",     // Darker orange for hover states
  primaryLight: "#F5A73C",     // Light orange for backgrounds

  green: "#3EA53E",            // Fresh green - nature, outdoors, growth
  greenLight: "#5CB85C",       // Light green - accents, success states
  greenDark: "#2E7D2E",        // Dark green - contrast, depth

  secondary: "#3EA53E",        // Green - secondary brand color
  secondaryHover: "#2E7D2E",   // Darker green for hover
  secondaryLight: "#5CB85C",   // Light green for backgrounds

  navy: "#2c2e3d",             // Legacy dark blue (backwards compat)
  navyLight: "#3d4a5c",        // Legacy light navy
  navyDark: "#1a2332",         // Legacy dark navy
  charcoal: "#333144",         // Dark charcoal (text headers)
  textGray: "#6d6e71",         // Body text gray
  darkBlue: "#2c2e3d",         // Dark blue for outlines/symbols/borders
  light: "#eef0f1",            // Light gray backgrounds
  white: "#ffffff",            // White backgrounds

  // Legacy aliases (for backward compatibility)
  accent: "#E48F15",           // Alias for primary (orange)
  logoShirt: "#3EA53E",        // Logo accent color (green)

  // Activity Colors (fun, playful for kids)
  activities: {
    bushcraft: {
      primary: "#8B4513",      // Brown - earthy, wood, survival
      light: "#A0522D",
      dark: "#654321",
    },
    campfire: {
      primary: "#FF6B35",      // Red-orange - fire, warmth, cooking
      light: "#FF8C5A",
      dark: "#E55527",
    },
    carpentry: {
      primary: "#CD853F",      // Tan/wood - traditional, building
      light: "#DAA520",
      dark: "#B8860B",
    },
    arts: {
      primary: "#9B59B6",      // Purple - creativity, imagination
      light: "#BB8FCE",
      dark: "#7D3C98",
    },
    walking: {
      primary: "#3EA53E",      // Green - nature, trails, exploration
      light: "#5CB85C",
      dark: "#2E7D2E",
    },
    denBuilding: {
      primary: "#6B8E23",      // Olive green - shelter, forest
      light: "#9ACD32",
      dark: "#556B2F",
    },
    challenges: {
      primary: "#E74C3C",      // Red - teamwork, problem solving
      light: "#EC7063",
      dark: "#C0392B",
    },
    wildlife: {
      primary: "#3EA53E",      // Green - animals, nature, discovery (using brand green)
      light: "#5CB85C",
      dark: "#2E7D2E",
    },
  },

  // Backwards compatibility shims (for MFS template pages we're not using)
  services: {
    photography: { primary: "#E48F15", light: "#F5A73C", dark: "#C67810", hover: "#C67810" },
    video: { primary: "#3EA53E", light: "#5CB85C", dark: "#2E7D2E", hover: "#2E7D2E" },
    videoProduction: { primary: "#3EA53E", light: "#5CB85C", dark: "#2E7D2E", hover: "#2E7D2E" },
    design: { primary: "#9B59B6", light: "#BB8FCE", dark: "#7D3C98", hover: "#7D3C98" },
    signage: { primary: "#CD853F", light: "#DAA520", dark: "#B8860B", hover: "#B8860B" },
    socialMedia: { primary: "#3EA53E", light: "#5CB85C", dark: "#2E7D2E", hover: "#2E7D2E" },
    marketingStrategy: { primary: "#E74C3C", light: "#EC7063", dark: "#C0392B", hover: "#C0392B" },
  },

  ecommerce: {
    primary: "#E48F15",
    primaryHover: "#C67810",
    secondary: "#333144",
    success: "#3EA53E",
    error: "#ef4444",
    neutral: {
      50: "#fafaf9",
      100: "#f5f5f4",
      200: "#e7e5e4",
      300: "#d6d3d1",
      600: "#57534e",
      700: "#44403c",
    },
  },

  // Typography Scale (matches Tailwind config)
  fontSize: {
    xs: '0.75rem',
    sm: '0.875rem',
    base: '1rem',
    lg: '1.125rem',
    xl: '1.25rem',
    '2xl': '1.5rem',
    '3xl': '1.875rem',
    '4xl': '2.25rem',
    '5xl': '3rem',
  },

  // Spacing (8px grid system)
  spacing: {
    xs: '0.25rem',   // 4px
    sm: '0.5rem',    // 8px
    md: '1rem',      // 16px
    lg: '1.5rem',    // 24px
    xl: '2rem',      // 32px
    '2xl': '3rem',   // 48px
    '3xl': '4rem',   // 64px
  },

  // Animation Durations
  animation: {
    fast: '150ms',
    normal: '300ms',
    slow: '500ms',
  },
} as const;

export function SectionHeading({ eyebrow, title, subtitle }: { eyebrow: string; title: string; subtitle?: string }) {
  return (
    <div className="mb-12 text-center">
      <div className="text-sm uppercase tracking-widest text-primary font-bold mb-3">{eyebrow}</div>
      <h2 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold text-charcoal-900 mb-4">{title}</h2>
      {subtitle && <p className="text-xl text-charcoal-400 max-w-3xl mx-auto">{subtitle}</p>}
    </div>
  );
}

export function LogoMark({ size = 120, className = "" }: { size?: number; className?: string }) {
  // Original image aspect ratio is 451 x 311, which is approximately 1.45:1
  const height = size / 1.45;

  return (
    <img
      src="/club-wild-logo.png"
      alt="Club Wild Logo"
      width={size}
      height={height}
      className={className}
      style={{ width: `${size}px`, height: `${height}px`, objectFit: 'contain' }}
    />
  );
}

export function PrimaryBtn({ className = "", children, ...props }: React.ButtonHTMLAttributes<HTMLButtonElement> & { className?: string }) {
  return (
    <button
      {...props}
      className={`inline-flex items-center justify-center px-8 py-4 bg-primary text-white font-bold text-lg hover:scale-105 hover:shadow-xl transition-all duration-300 rounded-full ${className}`}
      style={{
        backgroundColor: BRAND.orange,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = BRAND.orangeDark;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = BRAND.orange;
      }}
    >
      {children}
    </button>
  );
}

export function SecondaryBtn({ className = "", children, ...props }: React.ButtonHTMLAttributes<HTMLButtonElement> & { className?: string }) {
  return (
    <button
      {...props}
      className={`inline-flex items-center justify-center px-8 py-4 bg-white border-2 text-green font-bold text-lg hover:bg-green hover:text-white transition-all duration-300 rounded-full ${className}`}
      style={{
        borderColor: BRAND.green,
        color: BRAND.green,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = BRAND.green;
        e.currentTarget.style.color = 'white';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = 'white';
        e.currentTarget.style.color = BRAND.green;
      }}
    >
      {children}
    </button>
  );
}

export function GhostBtn({ className = "", children, ...props }: React.ButtonHTMLAttributes<HTMLButtonElement> & { className?: string }) {
  return (
    <button
      {...props}
      className={`inline-flex items-center gap-3 rounded-full px-6 py-3 text-charcoal-700 font-semibold hover:text-primary transition-all ${className}`}
    >
      {children}
    </button>
  );
}

/**
 * IconFrame - Wraps icons with consistent border styling
 * Matches the header icon style with rounded border frame
 */
export function IconFrame({
  children,
  size = "md",
  className = ""
}: {
  children: React.ReactNode;
  size?: "sm" | "md" | "lg";
  className?: string;
}) {
  const sizeClasses = {
    sm: "w-10 h-10",    // 40px - for smaller icons
    md: "w-12 h-12",    // 48px - standard (matches header)
    lg: "w-16 h-16",    // 64px - for larger showcase icons
  };

  return (
    <div
      className={`flex items-center justify-center ${sizeClasses[size]} rounded-lg border-2 border-darkBlue bg-white hover:bg-darkBlue/10 transition-all ${className}`}
    >
      {children}
    </div>
  );
}
